/**
 * Reviews slider (converted from the Webflow `.review-slider.w-slider`).
 *
 * Five review cards, horizontal slide transition (600ms), NOT looped, custom
 * prev / next arrows, nav dots hidden — matching ANIMATIONS.md §12b. Rebuilt
 * with React state + GSAP (no Webflow slider JS). The slide track is translated
 * to the active slide; arrows are disabled at the ends since the slider does not
 * loop.
 */
import type { FC } from 'react'

import { useEffect, useRef, useState } from 'react'

import { gsap } from '@/lib/gsap'

interface Review {
    name: string
    rating: string
    text: string
}

const REVIEWS: Review[] = [
    {
        name: 'Name',
        rating: '4.8',
        text: 'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.'
    },
    {
        name: 'Name',
        rating: '3.8',
        text: 'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.'
    },
    {
        name: 'Name',
        rating: '4.8',
        text: 'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.'
    },
    {
        name: 'Name',
        rating: '4.8',
        text: 'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.'
    },
    {
        name: 'Name',
        rating: '4.8',
        text: 'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.'
    }
]

export const ReviewsSlider: FC = () => {
    const maskRef = useRef<HTMLDivElement>(null)
    const [active, setActive] = useState(0)
    const firstRun = useRef(true)

    const slideTo = (index: number) => {
        setActive(Math.min(Math.max(index, 0), REVIEWS.length - 1))
    }

    /* Translate the slide track to the active slide (600ms slide). */
    useEffect(() => {
        const mask = maskRef.current
        if (!mask) return

        const slides = gsap.utils.toArray<HTMLElement>('.review-slide', mask)
        const target = slides[active]
        const base = mask.firstElementChild as HTMLElement | null
        if (!target || !base) return

        gsap.to(slides, {
            x: -(target.offsetLeft - base.offsetLeft),
            duration: firstRun.current ? 0 : 0.6,
            ease: 'power1.inOut',
            overwrite: 'auto'
        })

        firstRun.current = false
    }, [active])

    /* Keep the active slide aligned through viewport resizes. */
    useEffect(() => {
        const onResize = () => {
            const mask = maskRef.current
            if (!mask) return

            const slides = gsap.utils.toArray<HTMLElement>('.review-slide', mask)
            const target = slides[active]
            const base = mask.firstElementChild as HTMLElement | null
            if (!target || !base) return

            gsap.set(slides, { x: -(target.offsetLeft - base.offsetLeft) })
        }

        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [active])

    return (
        <div className='review-slider w-slider'>
            <div ref={maskRef} className='review-slider-mask w-slider-mask'>
                {REVIEWS.map((review, index) => (
                    <div key={index} className='review-slide w-slide' aria-hidden={index !== active}>
                        <div className='review-slider-holder'>
                            <div className='review-slider-header'>
                                <p className='review-name'>{review.name}</p>
                                <div className='slider-review-counter-container'>
                                    <h4 className='review-name'>{review.rating}</h4>
                                    <img src='/images/Star-Orange.svg' loading='lazy' alt='' className='slider-rating-star' />
                                </div>
                            </div>
                            <p className='paragraph-m grey-text---light'>{review.text}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div
                className='slider-left-arrow w-slider-arrow-left'
                role='button'
                tabIndex={0}
                aria-label='Previous review'
                aria-disabled={active === 0}
                onClick={() => slideTo(active - 1)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        slideTo(active - 1)
                    }
                }}
            >
                <img src='/images/Chevron-Left.svg' loading='lazy' alt='' className='arrow' />
            </div>

            <div
                className='slider-right-arrow w-slider-arrow-right'
                role='button'
                tabIndex={0}
                aria-label='Next review'
                aria-disabled={active === REVIEWS.length - 1}
                onClick={() => slideTo(active + 1)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        slideTo(active + 1)
                    }
                }}
            >
                <img src='/images/Chevron-Right.svg' loading='lazy' alt='' className='arrow' />
            </div>

            <div className='hide w-slider-nav w-round' />
        </div>
    )
}
